import { css, cx } from "@emotion/css";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { accessibilityStyles } from "../../../utils";
import { renderDescription, type DescriptionContent } from "../utils/descriptionWithLinks";
export interface FormDescriptionProps {
    /**
     * The description text to display. Can be a string or DescriptionContent for rich content.
     */
    description: string | DescriptionContent;
    /**
     * The ID of the description element.
     * This is important for accessiblituy and ensures that the input is correctly associated with the description.
     */
    id?: string;
    /**
     * Controls whether the description should appear disabled.
     */
    isDisabled?: boolean;
    /**
     * Controls whether the description should be styled for radio or checkbox fields.
     */
    isRadioOrCheckbox?: boolean;
    /**
     * Applies special styling for labels inside a legend.
     */
    isGroupLabel?: boolean;
    /**
     * Controls whether the description should be visually hidden.
     */
    hideDescription?: boolean;
}
/**
 *
 * FormDescription renders descriptive text for form fields, providing additional context
 * or instructions to users. This primitive handles various styling states including
 * disabled appearance, radio/checkbox positioning, and accessibility requirements.
 *
 * @internal
 */
export function FormDescription({ id, description, isDisabled, isRadioOrCheckbox, isGroupLabel, hideDescription }: FormDescriptionProps) {
    return (<p id={id} className={cx(helperTextStyles, {
            [legendDescriptionStyles]: isGroupLabel,
            [helperTextRadioOrCheckboxStyles]: isRadioOrCheckbox,
            [helperTextDisabledStyles]: isDisabled,
            [accessibleHiddenStyles]: hideDescription,
            // Used to add a classname that can be used to target for styling purposes
            ["hasHiddenDescription"]: hideDescription,
        })}>
            {typeof description === "string" ? description : renderDescription(description)}
        </p>);
}
const helperTextStyles = css({
    color: themeTokens.color.text.secondary,
    font: text.body.regular.large,
    margin: 0,
});
const legendDescriptionStyles = css({
    marginBottom: space[16],
});
const helperTextDisabledStyles = css({
    color: themeTokens.color.text.tertiary,
});
const helperTextRadioOrCheckboxStyles = css({
    gridColumn: "2 / -1",
});
const accessibleHiddenStyles = css({
    ...accessibilityStyles.hidden,
});
